// 📄 pages/Ariba/components/KnownErrorDetailModal.tsx
import React from 'react';
import { X, Download, FileText, Tag, ExternalLink, CalendarCheck } from 'lucide-react';
import { KnownError, KNOWN_ERROR_MODULES, sistemaAfectadoMeta, resolveNotaSapUrl } from '../../../services/knownErrorsService.ts';
import { formatFechaVerificacion } from '../../../utils/formatDate.ts';
import CopyIconButton from '../../../components/shared/CopyIconButton.tsx';

interface KnownErrorDetailModalProps {
  error: KnownError;
  onClose: () => void;
}

export default function KnownErrorDetailModal({ error, onClose }: KnownErrorDetailModalProps) {
  const moduleLabel = KNOWN_ERROR_MODULES.find(m => m.id === error.module)?.label || error.module;
  const sistema = sistemaAfectadoMeta(error.sistemaAfectado);
  const notaSapLink = resolveNotaSapUrl(error.notaSapUrl);
  const fechaVerificacion = (error.fechaVerificacion as any)?.toDate?.() as Date | undefined;
  const verificacion = fechaVerificacion ? formatFechaVerificacion(fechaVerificacion) : null;
  const images = error.attachments.filter(a => a.contentType.startsWith('image/'));
  const documents = error.attachments.filter(a => !a.contentType.startsWith('image/'));

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-y-auto shadow-2xl">
        <div className="flex items-start justify-between p-6 border-b border-white/10 sticky top-0 bg-[rgb(var(--c-surface))]">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-xs font-mono font-semibold text-blue-400">{error.code}</span>
              <span className="text-[10px] bg-white/5 text-gray-400 border border-white/10 px-2 py-0.5 rounded-full">{moduleLabel}</span>
              {sistema && (
                <span className="text-[10px] bg-sky-500/10 text-sky-300 border border-sky-500/30 px-2 py-0.5 rounded-full">{sistema.label}</span>
              )}
            </div>
            <h3 className="text-lg font-medium text-white">{error.title}</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Causa</h4>
            <p className="text-sm text-gray-300 whitespace-pre-wrap">{error.cause}</p>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Solución (paso a paso)</h4>
            <p className="text-sm text-gray-300 whitespace-pre-wrap">{error.solution}</p>
          </div>

          {(notaSapLink || error.versionAriba || verificacion) && (
            <div className="flex flex-wrap items-center gap-3 text-xs">
              {notaSapLink && (
                <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5">
                  <span className="text-gray-400">Nota SAP</span>
                  <CopyIconButton value={error.notaSapUrl || ''} title="Copiar número/URL" />
                  <a href={notaSapLink} target="_blank" rel="noopener noreferrer" title="Abrir nota" className="text-blue-400 hover:text-blue-300 flex items-center gap-1">
                    Abrir nota <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
              {error.versionAriba && (
                <span className="bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-gray-300">Versión Ariba: {error.versionAriba}</span>
              )}
              {verificacion && (
                <span className={`flex items-center gap-1.5 border rounded-lg px-2.5 py-1.5 ${verificacion.vencida ? 'text-amber-400 bg-amber-500/10 border-amber-500/30' : 'text-gray-300 bg-white/5 border-white/10'}`}>
                  <CalendarCheck className="w-3.5 h-3.5" />
                  Verificado el {verificacion.texto}
                </span>
              )}
            </div>
          )}

          {error.tags.length > 0 && (
            <div className="flex items-center flex-wrap gap-2">
              <Tag className="w-3.5 h-3.5 text-gray-500" />
              {error.tags.map(tag => (
                <span key={tag} className="text-[10px] bg-[rgba(91,155,240,0.1)] text-blue-400 border border-blue-400/20 px-2 py-0.5 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {images.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Capturas</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {images.map((img) => (
                  <a key={img.url} href={img.url} target="_blank" rel="noopener noreferrer">
                    <img
                      src={img.url}
                      alt={img.name}
                      className="w-full rounded-xl border border-white/10 hover:border-blue-400 transition-all"
                    />
                  </a>
                ))}
              </div>
            </div>
          )}

          {documents.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Documentos adjuntos</h4>
              <div className="space-y-2">
                {documents.map((docFile) => (
                  <a
                    key={docFile.url}
                    href={docFile.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 bg-white/5 border border-white/10 rounded-xl p-3 hover:border-blue-400 transition-all text-sm text-gray-300"
                  >
                    <FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    <span className="flex-1 truncate">{docFile.name}</span>
                    <Download className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
